import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPassageLabel } from '../../utils/passagePlans';

// Small centered modal opened by long-pressing a verse on Listen — lets the
// user add that single verse to one of their family's Passages Plans.
function AddToPlanPopover({ isOpen, onClose, verseRef, plans, defaultPlanId, onAdd, isFavorited, onToggleFavorite }) {
  const navigate = useNavigate();
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [added, setAdded] = useState(false);

  // Carries the held verse over to the create form so starting a brand new
  // plan doesn't lose the passage that prompted it.
  const goToCreatePlan = () => {
    onClose();
    const prefill = verseRef ? `?prefill=${encodeURIComponent(formatPassageLabel(verseRef))}` : '';
    navigate(`/passages-plans${prefill}`);
  };

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setAdded(false);
    const defaultStillValid = plans.some(p => p.id === defaultPlanId);
    setSelectedPlanId(defaultStillValid ? defaultPlanId : (plans[0]?.id || ''));
  }, [isOpen, plans, defaultPlanId]);

  if (!isOpen) return null;

  const handleAdd = async () => {
    if (!selectedPlanId) return;
    setSaving(true);
    setError('');
    try {
      await onAdd(selectedPlanId);
      setAdded(true);
      setTimeout(onClose, 900);
    } catch (err) {
      console.error('Error adding verse to plan:', err);
      setError(err.message || 'Failed to add verse to plan.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10001] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full sm:w-96 sm:mx-4 bg-white dark:bg-[#111827] rounded-t-3xl sm:rounded-2xl shadow-xl border border-gray-200 dark:border-white/10 p-5 pb-8 sm:pb-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Add to Passage Plan</h3>
          <div className="flex items-center gap-1">
            {onToggleFavorite && (
              <button
                onClick={onToggleFavorite}
                title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-400/10 transition-colors"
              >
                <svg className="w-5 h-5" fill={isFavorited ? '#ef4444' : 'none'} stroke="#ef4444" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white/70"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <p className="text-sm text-cerulean font-medium mb-4">
          {verseRef ? formatPassageLabel(verseRef) : ''}
        </p>

        {added ? (
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm font-medium py-3">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Added!
          </div>
        ) : plans.length === 0 ? (
          <div className="py-2">
            <p className="text-sm text-gray-500 dark:text-white/60 mb-3">
              Your family doesn't have any Passage Plans yet.
            </p>
            <button
              onClick={goToCreatePlan}
              className="w-full py-2.5 rounded-xl bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold text-sm"
            >
              Create a Passage Plan
            </button>
          </div>
        ) : (
          <>
            <label className="block text-xs font-medium text-gray-500 dark:text-white/50 mb-1">Plan</label>
            <select
              className="input-field mb-4"
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
            >
              {plans.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>

            {error && (
              <p className="text-xs text-red-600 dark:text-red-400 mb-3">{error}</p>
            )}

            <button
              onClick={handleAdd}
              disabled={saving || !selectedPlanId}
              className="w-full py-2.5 rounded-xl bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold text-sm disabled:opacity-50"
            >
              {saving ? 'Adding…' : 'Add to Plan'}
            </button>

            <button
              onClick={goToCreatePlan}
              className="w-full mt-2 py-2 text-xs font-medium text-cerulean hover:text-cerulean/80"
            >
              + Create a new plan instead
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default AddToPlanPopover;
